import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

/**
 * Cookie consent status
 * - 'pending': User has not yet made a choice
 * - 'accepted': User accepted cookies
 * - 'rejected': User rejected cookies
 */
export type ConsentStatus = "pending" | "accepted" | "rejected";

/**
 * Cookie preferences by category
 */
export interface CookiePreferences {
  /** Analytics cookies (PostHog) */
  analytics: boolean;
}

interface CookieConsentState {
  /** Current consent status */
  status: ConsentStatus;
  /** Cookie preferences by category */
  preferences: CookiePreferences;

  // Actions
  /** Accept all cookies */
  acceptAll: () => void;
  /** Reject all cookies */
  rejectAll: () => void;
  /** Update specific cookie preferences */
  updatePreferences: (preferences: Partial<CookiePreferences>) => void;
  /** Reset consent to pending state (useful for testing) */
  resetConsent: () => void;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  analytics: false,
};

/**
 * In-memory storage fallback for when localStorage is unavailable
 * (e.g., Safari private browsing mode).
 * Hoisted to module scope to maintain state across rehydration/HMR.
 */
const memoryStore: Record<string, string> = {};
const memoryStorage = {
  getItem: (key: string) => memoryStore[key] ?? null,
  setItem: (key: string, value: string) => {
    memoryStore[key] = value;
  },
  removeItem: (key: string) => {
    delete memoryStore[key];
  },
};

/**
 * Creates a CookiePreferences object with all keys set to the specified value.
 * This ensures type safety when enabling/disabling all preferences.
 */
function createPreferences(value: boolean): CookiePreferences {
  return (Object.keys(DEFAULT_PREFERENCES) as Array<keyof CookiePreferences>).reduce((acc, key) => {
    acc[key] = value;
    return acc;
  }, {} as CookiePreferences);
}

export const useCookieConsentStore = create<CookieConsentState>()(
  devtools(
    persist(
      (set) => ({
        status: "pending",
        preferences: DEFAULT_PREFERENCES,

        acceptAll: () =>
          set({
            status: "accepted",
            preferences: createPreferences(true),
          }),

        rejectAll: () =>
          set({
            status: "rejected",
            preferences: createPreferences(false),
          }),

        updatePreferences: (newPreferences) =>
          set((state) => {
            // If no preferences provided, don't change anything
            if (Object.keys(newPreferences).length === 0) {
              return state;
            }

            const updatedPreferences = {
              ...state.preferences,
              ...newPreferences,
            };

            // Check if preferences actually changed
            const hasChanged = Object.keys(updatedPreferences).some(
              (key) =>
                updatedPreferences[key as keyof CookiePreferences] !==
                state.preferences[key as keyof CookiePreferences],
            );

            // If nothing changed, preserve current state (including pending)
            if (!hasChanged) {
              return state;
            }

            // Determine status based on whether any cookies are enabled
            const hasAnyEnabled = Object.values(updatedPreferences).some(Boolean);

            return {
              preferences: updatedPreferences,
              // If user enables any cookie, they've accepted; otherwise rejected
              status: hasAnyEnabled ? "accepted" : "rejected",
            };
          }),

        resetConsent: () =>
          set({
            status: "pending",
            preferences: DEFAULT_PREFERENCES,
          }),
      }),
      {
        name: "cookie-consent-storage",
        storage: createJSONStorage(() => {
          // Feature-test localStorage for Safari private browsing mode
          // Safari throws on setItem even if localStorage is accessible
          const testKey = "__storage_test__";
          try {
            localStorage.setItem(testKey, testKey);
            localStorage.removeItem(testKey);
            return localStorage;
          } catch {
            // Use module-scoped fallback to maintain state across rehydration/HMR
            return memoryStorage;
          }
        }),
      },
    ),
    { name: "cookie-consent" },
  ),
);

/**
 * Hook to check if user has made a consent decision
 */
export const useHasConsentDecision = () => {
  const status = useCookieConsentStore((state) => state.status);
  return status !== "pending";
};

/**
 * Hook to check if analytics cookies are enabled
 */
export const useAnalyticsConsent = () => {
  return useCookieConsentStore((state) => state.preferences.analytics);
};
